import {
  BasicType,
  ElementCategory,
  ElementType,
} from "@easy-email-pro-core/constants";
import {
  ColumnElement,
  Element as ElementTypes,
  SectionElement,
  StandardTextListElement,
  TextElement,
  TextNode,
  ElementCategoryType,
  Element,
  GroupElement,
  PlaceholderElement,
  WrapperElement,
  SocialElement,
  PageElement,
  HeroElement,
  ImageElement,
  ButtonElement,
  ContentWidgetElement,
  SectionWidgetElement,
  StandardParagraphElement,
  StandardH1Element,
  StandardH2Element,
  StandardH3Element,
  StandardH4Element,
  HtmlNodeElement,
  LineBreakElement,
  MergetagElement,
  TableElement,
  StandardTable2TdElement,
  StandardTable2TrElement,
  StandardTable2Element,
  StandardNavbarLinkElement,
  PageHeaderElement,
  PageFooterElement,
} from "@easy-email-pro-core/typings";
import { get } from "lodash";
import type { Node } from "slate";
import { BlockManager } from "./BlockManager";
import { EditorCore } from "./EditorCore";

const unselectableCategoryType = [
  ElementCategory.COLUMN,
  ElementCategory.GROUP,
  ElementCategory.UNSET,
  ElementCategory.TEXT_LIST_ITEM,
  ElementCategory.UNKNOWN_CONTENT,
] as string[];

const contentCategoryType = [
  ElementCategory.DANGEROUS_CODE,
  ElementCategory.TABLE,
  ElementCategory.TEXT,
  ElementCategory.BUTTON,
  ElementCategory.DIVIDER,
  ElementCategory.IMAGE,
  ElementCategory.NAVBAR,
  ElementCategory.SOCIAL,
  ElementCategory.SPACER,
  ElementCategory.TEXT_LIST,
  ElementCategory.TEXT_LIST_ITEM,
  ElementCategory.UNKNOWN_CONTENT,
  ElementCategory.TABLE2_TD,
] as string[];

export class NodeUtils {
  static getPageIdx() {
    return "content";
  }

  static getUniversalElementsIdx(appId: string) {
    return `blocks.${appId}`;
  }

  static getChildIdx(idx: string, index: number) {
    return `${idx}.children.${index}`;
  }

  static getNodeIdxClassName(idx: string) {
    return `node-idx-${idx}`;
  }

  static getParentIdx(idx: string): string {
    if (idx === NodeUtils.getPageIdx()) return "";
    return /(.*)\.children\.\d+$/.exec(idx)?.[1] || "";
  }

  static isVoidBlockElement(node: TextNode | Node) {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);

    return Boolean(block.void);
  }

  static isUnsetElement(node: TextNode | Node) {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);

    return (
      block.category === ElementCategory.UNSET ||
      block.category === ElementCategory.SOCIAL_ELEMENT ||
      block.category === ElementCategory.NAVBAR_LINK
    );
  }

  static isTable2Element(node: TextNode | Node): node is StandardTable2Element {
    if (!NodeUtils.isElement(node)) return false;

    return node.type === ElementType.STANDARD_TABLE2;
  }

  static isTable2TrElement(
    node: TextNode | Node
  ): node is StandardTable2TrElement {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);

    return block.category === ElementCategory.TABLE2_TR;
  }

  static isTable2TdElement(
    node: TextNode | Node
  ): node is StandardTable2TdElement {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);

    return block.category === ElementCategory.TABLE2_TD;
  }

  static isTextNode(node: TextNode | Node): node is TextNode {
    return Boolean(get(node, "text") !== undefined);
  }

  static isElement(node: TextNode | Node): node is ElementTypes {
    return Boolean(
      get(node, "type") && get(node, "children") && get(node, "attributes")
    );
  }

  static isHTMLNodeElement(node: TextNode | Node): node is HtmlNodeElement {
    return NodeUtils.isElement(node) && node.type === ElementType.HTML_NODE;
  }

  static isHTMLBlockNodeElement(
    node: TextNode | Node
  ): node is HtmlNodeElement {
    return (
      NodeUtils.isElement(node) && node.type === ElementType.HTML_BLOCK_NODE
    );
  }

  static isBlockElement(node: TextNode | Node): node is ElementTypes {
    const type = get(node, "type");
    const block = BlockManager.getBlockByType(type as any);
    if (!block) return false;
    return (
      !block.inlineElement &&
      !NodeUtils.isHTMLBlockNodeElement(node) &&
      !NodeUtils.isTable2TrElement(node) &&
      !NodeUtils.isTable2TdElement(node)
    );
  }

  static isLineBreakElement(node: TextNode | Node): node is LineBreakElement {
    const type = get(node, "type");
    return type === ElementType.LINE_BREAK;
  }

  static isUniversalElement(
    node: TextNode | Node
  ): node is ElementTypes & { uid: string } {
    if (!NodeUtils.isElement(node)) return false;
    return Boolean(node.uid);
  }
  static isStandaloneElement(
    node: TextNode | Node
  ): node is ElementTypes & { uid: string } {
    if (!NodeUtils.isElement(node)) return false;
    return Boolean(BlockManager.getBlockByType(node.type)?.standalone);
  }

  static isInlineElement(node: TextNode | Node): node is ElementTypes {
    if (!NodeUtils.isElement(node)) return false;
    const type = get(node, "type");
    const block = BlockManager.getBlockByType(type);
    if (!block) return false;
    return !!block.inlineElement;
  }

  static isMergetagElement(node: TextNode | Node): node is MergetagElement {
    if (!NodeUtils.isElement(node)) return false;
    const type = get(node, "type");
    const block = BlockManager.getBlockByType(type);
    if (!block) return false;
    return !!block.inlineElement && block.type === BasicType.MERGETAG;
  }

  static isPageHeaderElement(node: TextNode | Node): node is PageHeaderElement {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    if (!block) return false;
    return block.category === ElementCategory.PAGE_HEADER;
  }

  static isPageFooterElement(node: TextNode | Node): node is PageFooterElement {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    if (!block) return false;
    return block.category === ElementCategory.PAGE_FOOTER;
  }

  static isContentElementCategory = (type: ElementCategoryType) => {
    return contentCategoryType.includes(type);
  };

  static isPageElement = (node: Node | TextNode): node is PageElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.PAGE;
  };

  static isSectionElement = (node: TextNode | Node): node is SectionElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.SECTION;
  };

  static isTableElement = (node: TextNode | Node): node is TableElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.TABLE;
  };
  static isHeroElement = (node: TextNode | Node): node is HeroElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.HERO;
  };

  static isNavbarLink = (
    node: TextNode | Node
  ): node is StandardNavbarLinkElement => {
    if (!NodeUtils.isElement(node)) return false;
    return (
      node.type === ElementType.NAVBAR_LINK ||
      node.type === ElementType.STANDARD_NAVBAR_LINK
    );
  };

  static isDividerElement = (node: TextNode | Node): node is ButtonElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.DIVIDER;
  };

  static isSpacerElement = (node: TextNode | Node): node is ButtonElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.SPACER;
  };

  static isButtonElement = (node: TextNode | Node): node is ButtonElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category.includes(ElementCategory.BUTTON);
  };

  static isImageElement = (node: TextNode | Node): node is ImageElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.IMAGE;
  };

  static isSocialElement = (node: TextNode | Node): node is SocialElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.SOCIAL;
  };

  static isWidgetElement = (
    node: TextNode | Node
  ): node is ContentWidgetElement | SectionWidgetElement => {
    if (!NodeUtils.isElement(node)) return false;
    return (
      [
        ElementType.CONTENT_WIDGET,
        ElementType.SECTION_WIDGET,
        ElementType.WRAPPER_WIDGET,
      ] as Element["type"][]
    ).includes(node.type);
  };

  static isWrapperElement = (node: TextNode | Node): node is WrapperElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category.includes(ElementCategory.WRAPPER);
  };

  static isRawElement = (node: TextNode | Node): node is GroupElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category.includes(ElementCategory.RAW);
  };

  static isGroupElement = (node: TextNode | Node): node is GroupElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.GROUP;
  };

  static isColumnElement = (node: TextNode | Node): node is ColumnElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);

    return block.category === ElementCategory.COLUMN;
  };

  static isContentElement = (node: TextNode | Node): node is Element => {
    if (!NodeUtils.isElement(node)) return false;
    if (node.type === BasicType.PLACEHOLDER) return true;
    const block = BlockManager.getBlockByType(node.type);

    return contentCategoryType.includes(block.category);
  };

  static isAutoCompleteElement = (node: TextNode | Node): node is Element => {
    if (!NodeUtils.isElement(node)) return false;
    if (node.type === BasicType.PLACEHOLDER) return true;
    const block = BlockManager.getBlockByType(node.type);

    const type = block.category;
    return (
      contentCategoryType.includes(type) && type !== ElementCategory.TABLE2_TD
    );
  };

  static isUnelectableElement = (node: TextNode | Node) => {
    if (!NodeUtils.isElement(node)) return false;
    if (node.type === BasicType.PLACEHOLDER) return true;
    const block = BlockManager.getBlockByType(node.type);

    return unselectableCategoryType.includes(block.category);
  };

  static isTextElement = (
    node: TextNode | Node
  ): node is
    | TextElement
    | StandardParagraphElement
    | StandardH1Element
    | StandardH2Element
    | StandardH3Element
    | StandardH4Element
    | StandardTable2TdElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return (
      NodeUtils.isTable2TdElement(node) ||
      block.category === ElementCategory.TEXT ||
      block.category === ElementCategory.TEXT_LIST ||
      block.category === ElementCategory.TEXT_LIST_ITEM ||
      block.category === ElementCategory.INLINE_TEXT
    );
  };

  static isPlaceholderElement = (
    node: TextNode | Node
  ): node is PlaceholderElement => {
    if (!NodeUtils.isElement(node)) return false;
    return node.type === BasicType.PLACEHOLDER;
  };

  static isTextListElement = (
    node: TextNode | Node
  ): node is StandardTextListElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.TEXT_LIST;
  };

  static isTextListItemElement = (
    node: TextNode | Node
  ): node is StandardTextListElement => {
    if (!NodeUtils.isElement(node)) return false;
    const block = BlockManager.getBlockByType(node.type);
    return block.category === ElementCategory.TEXT_LIST_ITEM;
  };

  static isParentCategory(
    cat: ElementCategoryType,
    parentCat: ElementCategoryType
  ) {
    if (
      cat === ElementCategory.BLOCK_NODE &&
      parentCat === ElementCategory.BLOCK_NODE
    ) {
      return true;
    }

    if (
      [
        ElementCategory.INLINE_TEXT,
        ElementCategory.TEXT_LIST,
        ElementCategory.BLOCK_NODE,
      ].includes(cat as any) &&
      NodeUtils.isContentElementCategory(parentCat)
    ) {
      return true;
    }

    if (NodeUtils.isContentElementCategory(cat)) {
      // if autoComplete is true, then return true
      if (EditorCore.autoComplete) {
        if (
          parentCat === ElementCategory.PAGE ||
          parentCat === ElementCategory.WRAPPER
        ) {
          return true;
        }
      }

      return (
        parentCat === ElementCategory.COLUMN ||
        parentCat === ElementCategory.HERO
      );
    }
    switch (cat) {
      case ElementCategory.COLUMN:
        return (
          parentCat === ElementCategory.GROUP ||
          parentCat === ElementCategory.SECTION
        );
      case ElementCategory.GROUP:
        return parentCat === ElementCategory.SECTION;
      case ElementCategory.SECTION:
        return (
          parentCat === ElementCategory.WRAPPER ||
          parentCat === ElementCategory.PAGE
        );
      case ElementCategory.HERO:
        return (
          parentCat === ElementCategory.WRAPPER ||
          parentCat === ElementCategory.PAGE
        );
      case ElementCategory.WRAPPER:
        return parentCat === ElementCategory.PAGE;
      case ElementCategory.SOCIAL_ELEMENT:
        return parentCat === ElementCategory.SOCIAL;
      case ElementCategory.NAVBAR_LINK:
        return parentCat === ElementCategory.NAVBAR;
    }
    return false;
  }

  static isParentCategoryType(
    childType: Element["type"],
    parentType: Element["type"]
  ) {
    const childBlock = BlockManager.getBlockByType(childType);
    const parentBlock = BlockManager.getBlockByType(parentType);

    if (
      [ElementType.HTML_BLOCK_NODE, ElementType.HTML_NODE].includes(
        childType as any
      ) &&
      NodeUtils.isContentElement(parentBlock.create())
    ) {
      return true;
    }

    if (
      childBlock.inlineElement &&
      [ElementType.HTML_BLOCK_NODE, ElementType.HTML_NODE].includes(
        parentType as any
      )
    ) {
      return true;
    }

    return this.isParentCategory(childBlock.category, parentBlock.category);
  }
}
